import { useState } from 'react';
import { UploadedFile } from 'types';
import { FileUploadProgress, UploadToStorageRequest } from '../types';
import { getTotalBytes } from '../util';
import { MAX_FILE_SIZE } from '../../../constants';
import { AxiosProgressEvent } from 'axios';
import { uploadFileToStorageApi } from 'api';

export interface UseFileUploadProps {
  onUploadComplete?: (uploadedFiles: UploadedFile[]) => void;
  onUploadFailed?: () => void;
}

const DEFAULT_PROGRESS: FileUploadProgress = {
  percent: 0,
  bytesUploaded: 0,
  totalBytes: 0,
};

export interface UseFileUploadReturn {
  uploadFile: (
    file: File,
    groupingReference: string,
    overwriteExistingFile: boolean
  ) => Promise<void>;
  files: File[] | undefined;
  progress: FileUploadProgress;
}

export const useFileUpload = ({
  onUploadComplete,
  onUploadFailed,
}: UseFileUploadProps): UseFileUploadReturn => {
  const [progress, setProgress] = useState<FileUploadProgress>(DEFAULT_PROGRESS);
  const [files, setFiles] = useState<File[]>();

  const uploadFileToStorage = async (
    file: File,
    groupingReference: string,
    overwriteExistingFile = false
  ): Promise<void> => {
    setFiles([file]);

    const totalBytes = getTotalBytes([file]);

    const onUploadProgress = (progressEvent: AxiosProgressEvent) => {
      if (!progressEvent.lengthComputable) return;
      const percentage: number = Math.round(
        (progressEvent.loaded * 100) / (progressEvent.total || 1)
      );
      setProgress({
        percent: percentage,
        bytesUploaded: totalBytes * (percentage / 100),
        totalBytes: totalBytes,
      });
    };

    const uploadToStorageRequest: UploadToStorageRequest = {
      onUploadProgress,
      file,
      groupingReference,
      metadata: { MimeType: file.type },
      overwriteExistingFile,
    };

    try {
      if (totalBytes > MAX_FILE_SIZE) {
        throw new Error();
      }
      setProgress(DEFAULT_PROGRESS);
      const uri = await uploadFileToStorageApi(uploadToStorageRequest);
      setFiles(undefined);
      onUploadComplete?.([{ file, uri, uploadedDate: new Date() }]);
      setProgress(DEFAULT_PROGRESS);
    } catch (err) {
      setFiles(undefined);
      setProgress(DEFAULT_PROGRESS);
      onUploadFailed?.();
    }
  };

  return {
    uploadFile: uploadFileToStorage,
    files,
    progress,
  };
};
